import { faWrench } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useState } from 'react'
import { useQuery } from 'urql'
import { GetJobPhotosQuery } from '../../generated/user/graphql'
import useIsMobile from '../../hooks/useIsMobile'
import { GET_JOB_PHOTOS_QUERY } from '../../pages/EstimatesFlow/EstimatesFlow.gql'
import { OnsiteBasicModal } from '../../ui/OnsiteModal/OnsiteModal'
import { LoadingSpinner } from '../LoadingSpinner'
import { AsyncPhotoUpload, AsyncTakePhoto } from '../Upload/AsyncPhotoUpload'
import { LoadingImageData } from '../Upload/LoadingImage'
import { UploadButton } from '../Upload/Upload'

// The only data we save for a checklist photo (either from the photo input or an associated photo) is just the CDN URL.
// But we often need a better way to find which photo we're talking about. For instance, this photo upload modal needs
// to know which form item this photo is for. It is either a checklist item, or a sub-item in a dual-column section. If
// the item type is photo, we know it's the main value, otherwise we know it's an associated photo (since they are
// mutually exclusive).
export type PhotoSelector = {
  itemIndex: number
  side?: 'left' | 'right'
  rowIndex?: number
}

type FromJobImagePickerProps = {
  jobPhotos?: GetJobPhotosQuery['photoLinks']
  onPhotoUploadChange: (photoData: LoadingImageData) => void
}

const FromJobImagePicker = React.memo<FromJobImagePickerProps>(({ jobPhotos, onPhotoUploadChange }) => (
  <div>
    {jobPhotos?.length ? (
      <div className="grid grid-cols-3 gap-2">
        {jobPhotos.map(({ photo }) => (
          <div
            key={photo.photoGuid}
            className="cursor-pointer"
            onClick={() => onPhotoUploadChange({ cdnUrl: photo.cdnUrl, fromJob: true })}
          >
            <img src={photo.cdnUrl} alt={photo.cdnUrl} className="h-auto w-full" />
          </div>
        ))}
      </div>
    ) : (
      <div>No photos available for this job</div>
    )}
  </div>
))

type ChecklistPhotoUploadModalProps = {
  onClose: () => void
  photoSelector: PhotoSelector
  onPhotoUploadChange: (photoSelector: PhotoSelector, photoData: LoadingImageData) => void
  jobGuid?: string
}

export const ChecklistPhotoUploadModal = React.memo<ChecklistPhotoUploadModalProps>(
  ({ onClose, photoSelector, onPhotoUploadChange: externalOnPhotoUploadChange, jobGuid }) => {
    const isMobile = useIsMobile()
    const [fromJobPickerOpen, setFromJobPickerOpen] = useState(false)

    const [{ data: jobPhotos, fetching: fetchingJobPhotos }] = useQuery({
      query: GET_JOB_PHOTOS_QUERY,
      // I know jobGuid will be defined because of `pause`. But `?? ''` for typescript
      variables: { jobGuid: jobGuid ?? '' },
      pause: !jobGuid,
    })

    const onPhotoUploadChange = useCallback(
      (photoData: LoadingImageData) => externalOnPhotoUploadChange(photoSelector, photoData),
      [externalOnPhotoUploadChange, photoSelector],
    )

    return (
      <OnsiteBasicModal
        header={fromJobPickerOpen ? 'Job photos' : 'Add photo'}
        onClose={onClose}
        onBack={fromJobPickerOpen ? () => setFromJobPickerOpen(false) : undefined}
      >
        {fromJobPickerOpen ? (
          fetchingJobPhotos ? (
            <LoadingSpinner />
          ) : (
            <FromJobImagePicker jobPhotos={jobPhotos?.photoLinks} onPhotoUploadChange={onPhotoUploadChange} />
          )
        ) : (
          <div className="space-y-3">
            <AsyncPhotoUpload onPhotoUploadChange={onPhotoUploadChange} />
            {!!jobGuid && !!jobPhotos?.photoLinks.length && (
              <UploadButton onClick={() => setFromJobPickerOpen(true)} icon={<FontAwesomeIcon icon={faWrench} />}>
                Choose from job
              </UploadButton>
            )}
            {isMobile && <AsyncTakePhoto onPhotoUploadChange={onPhotoUploadChange} />}
          </div>
        )}
      </OnsiteBasicModal>
    )
  },
)

ChecklistPhotoUploadModal.displayName = 'ChecklistPhotoUploadModal'
